/**
 * Add client's refusals as sentences: the create route's codes by the
 * box they concern, a 400 by its field, anything else the house save
 * sentence. The body's message is never read, and no sentence names a
 * setting, a key or a column.
 */

import type { CreateClientBody } from './addClient';
import { ApiError } from './api';
import { contactSentence, errorCodeOf, fieldOf, pageWriteText, type SupportContact } from './errorCopy';
import { fieldSentence } from './fieldErrorCopy';

/** The boxes of the Add client dialog a refusal can read under. */
export type RefusalBox = 'first' | 'last' | 'preferred' | 'service' | 'cm' | 'therapist' | 'pronouns' | 'rate_set';

/** One sentence per refusal code. `{contact_admin}` and `{contact_billing}`
 *  are the support contact for that category.
 *  The rate sentences reach only a seat offered a rate set, and neither
 *  names an amount. */
export const CLIENT_CREATE_REFUSALS: Readonly<Record<string, string>> = {
  client_company_unconfigured:
    "Adding clients isn't set up yet. Ask {contact_admin} to finish the setup.",
  not_a_care_manager: 'That person is not an active care manager. Pick another, or choose No care manager.',
  not_a_spice_therapist: 'That person is not an active SPICE therapist. Pick another, or choose No SPICE therapist.',
  unknown_rate_set: "That rate set isn't available any more. Pick another, or choose No rate set.",
  unknown_rate_band: "That rate set can't be used as it stands. Choose No rate set, and ask {contact_billing} to check it.",
};

/** The box each named code concerns; null reads on the alert line. */
const BOX_OF_CODE: Readonly<Record<string, RefusalBox | null>> = {
  client_company_unconfigured: null,
  not_a_care_manager: 'cm',
  not_a_spice_therapist: 'therapist',
  unknown_rate_set: 'rate_set',
  unknown_rate_band: 'rate_set',
};

/** The create body's keys, each with its box and the label the box shows. */
const FIELDS: Readonly<Record<string, { box: RefusalBox; label: string }>> = {
  first_name: { box: 'first', label: 'First name' },
  last_name: { box: 'last', label: 'Last name' },
  preferred_name: { box: 'preferred', label: 'Preferred name' },
  engagement: { box: 'service', label: 'Service' },
  primary_cm_roster_id: { box: 'cm', label: 'Care manager' },
  spice_therapist_roster_id: { box: 'therapist', label: 'SPICE therapist' },
  pronouns: { box: 'pronouns', label: 'Pronouns' },
  rate_set_id: { box: 'rate_set', label: 'Rate set' },
};

const NAME_LIMIT = 100;

/** The sentence for a refused field, from the value the screen sent. A
 *  blank First name reads "Fill in"; every other name case reads its
 *  one-line rule; a list box reads "Choose … from the list." */
function fieldRefusalText(field: string, label: string, sent: CreateClientBody, contact: SupportContact): string {
  const value = (sent as unknown as Record<string, unknown>)[field];
  if (field === 'first_name' && (typeof value !== 'string' || value.trim() === '')) {
    return fieldSentence(label, { kind: 'text' }, value, contact);
  }
  if (field === 'first_name' || field === 'last_name' || field === 'preferred_name') {
    return fieldSentence(label, { kind: 'one_line', max: NAME_LIMIT }, value, contact);
  }
  return fieldSentence(label, { kind: 'from_list' }, value, contact);
}

/** An Add client refusal as one sentence and the box it reads under:
 *  a named code by its box, a 400 by its field, anything else the house
 *  save sentence on the alert line. */
export function clientCreateRefusal(
  e: unknown,
  contact: SupportContact,
  sent: CreateClientBody,
): { box: RefusalBox | null; text: string } {
  const code = errorCodeOf(e);
  const sentence = code === null ? undefined : CLIENT_CREATE_REFUSALS[code];
  if (code !== null && sentence !== undefined) {
    return { box: BOX_OF_CODE[code] ?? null, text: contactSentence(sentence, contact) };
  }
  const field = fieldOf(e);
  const known = field === null ? undefined : FIELDS[field];
  if (field !== null && known !== undefined && e instanceof ApiError && e.status === 400) {
    return { box: known.box, text: fieldRefusalText(field, known.label, sent, contact) };
  }
  return { box: null, text: pageWriteText(e, contact) };
}
